import { ref, readonly, computed } from 'vue'
import {
  FormatSettings,
  DEFAULT_FORMAT_SETTINGS,
  sanitizeFormatSettings,
} from '@/types/format-settings'
import * as core from '@/utils/format/formatCore'
import type {
  DateLike,
  DateFormatOptions,
  DateTimeFormatOptions,
  NumberFormatOptions,
  CurrencyFormatOptions,
} from '@/utils/format/formatCore'

/**
 * Capa reactiva sobre el core de formateo.
 *
 * El estado es un ref a nivel de MÓDULO (no dentro de useFormat), así que:
 *  - es un singleton compartido por toda la app,
 *  - las funciones exportadas sirven desde código no-componente (PDF, Excel,
 *    stores, utils) donde no hay setup().
 *
 * Las funciones vinculadas leen settings.value en tiempo de LLAMADA, por eso
 * siguen siendo reactivas dentro de un computed y a la vez usables desde un
 * módulo plano.
 *
 * La carga se engancha al watch de selectedCompanyId en @/stores/company.
 */

const CACHE_PREFIX = 'formatSettings:'
const COMPANY_ID_KEY = 'selectedCompanyId'

/**
 * Lee la config cacheada de la última compañía seleccionada, sincrónicamente al
 * cargar el módulo. Esto es lo que evita el flash: el primer paint usa el último
 * valor bueno conocido y Firestore reconcilia después en silencio.
 *
 * selectedCompanyId es useLocalStorage en el company store, así que está
 * disponible sincrónicamente aquí.
 */
function readCache(): FormatSettings | null {
  try {
    const rawCompanyId = localStorage.getItem(COMPANY_ID_KEY)
    if (!rawCompanyId) return null

    // useLocalStorage serializa los strings como JSON
    let companyId = rawCompanyId
    try {
      const parsed = JSON.parse(rawCompanyId)
      if (typeof parsed === 'string') companyId = parsed
    } catch {
      // no era JSON: se usa el string crudo
    }
    if (!companyId) return null

    const cached = localStorage.getItem(`${CACHE_PREFIX}${companyId}`)
    if (!cached) return null

    return sanitizeFormatSettings(JSON.parse(cached))
  } catch {
    return null
  }
}

const settings = ref<FormatSettings>(readCache() ?? { ...DEFAULT_FORMAT_SETTINGS })
const isReady = ref(false)

/**
 * Lectura imperativa para código fuera de componentes (PDF, Excel, stores).
 * En un componente preferí useFormat() o las funciones vinculadas.
 */
export function getFormatSettings(): FormatSettings {
  return settings.value
}

/**
 * Aplica settings (crudos de Firestore) y los cachea para el próximo arranque.
 * Repinta toda la app al instante por reactividad.
 */
export function setFormatSettings(
  raw: Partial<FormatSettings> | null | undefined,
  companyId?: string | null
): void {
  settings.value = sanitizeFormatSettings(raw)
  isReady.value = true

  if (!companyId) return
  try {
    localStorage.setItem(`${CACHE_PREFIX}${companyId}`, JSON.stringify(settings.value))
  } catch {
    // cuota llena o storage bloqueado: el cache es best-effort
  }
}

export function resetFormatSettings(): void {
  settings.value = { ...DEFAULT_FORMAT_SETTINGS }
  isReady.value = false
}

// Funciones vinculadas: reactivas en computeds, usables desde módulos planos.
export const formatNumber = (value: number | string | null | undefined, opts?: NumberFormatOptions): string =>
  core.formatNumber(value, settings.value, opts)

export const formatCurrency = (value: number | string | null | undefined, opts?: CurrencyFormatOptions): string =>
  core.formatCurrency(value, settings.value, opts)

export const parseNumber = (input: string | number | null | undefined): number | null =>
  core.parseNumber(input, settings.value)

export const parseNumberOr = (input: string | number | null | undefined, fallback: number): number =>
  core.parseNumberOr(input, settings.value, fallback)

export const formatDate = (value: DateLike, opts?: DateFormatOptions): string =>
  core.formatDate(value, settings.value, opts)

/** Como formatDate pero con los componentes en zona LOCAL. Para datepickers. */
export const formatLocalDate = (value: DateLike, opts?: DateFormatOptions): string =>
  core.formatLocalDate(value, settings.value, opts)

export const formatDateTime = (value: DateLike, opts?: DateTimeFormatOptions): string =>
  core.formatDateTime(value, settings.value, opts)

export const formatTime = (value: DateLike, opts?: DateTimeFormatOptions): string =>
  core.formatTime(value, settings.value, opts)

export const parseDate = (input: string): string | null =>
  core.parseDate(input, settings.value)

export function useFormat() {
  return {
    settings: readonly(settings),
    isReady: readonly(isReady),
    /** Ej. "1.234,56" — para hints y placeholders en la UI. */
    numberSample: computed(() => core.formatNumber(1234.56, settings.value, { intent: 'money' })),

    formatNumber,
    formatCurrency,
    parseNumber,
    parseNumberOr,
    formatDate,
    formatLocalDate,
    formatDateTime,
    formatTime,
    parseDate,
  }
}
